import {
    createFileRoute,
    redirect,
    stripSearchParams,
} from '@tanstack/react-router';
import LevelInfo from './-LevelInfo';
import RecordsInfo from './-LevelRecords';
import {
    levelCreatorsQuery,
    levelHistoryQuery,
    levelNotesQuery,
    levelQuery,
    levelRecordsQuery,
    levelUpdatesQuery,
} from '~/util/queries/public/levels';
import { Suspense, useEffect } from 'react';
import { getFullLevelThumbnail, seo, trimStringToNull } from '~/util';
import { useBackground } from '~/components/Providers/BackgroundProvider';
import Load from '~/components/Load';
import { DefaultNotFound } from '~/components/DefaultNotFound';
import { defaultRecordsParams, recordsParamsSchema } from './-SearchParams';
import { hasScope } from '~/auth/utils';

export const Route = createFileRoute('/list/$levelid')({
    validateSearch: recordsParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultRecordsParams)],
    },
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient, auth },
        params: { levelid },
        deps: { list },
    }) => {
        const level = await queryClient.fetchQuery(
            levelQuery({ list, levelid }),
        );

        if (!level) {
            throw redirect({
                to: '/list',
                replace: true,
            });
        }

        queryClient.prefetchQuery(levelCreatorsQuery({ list, levelid }));
        queryClient.prefetchQuery(
            levelRecordsQuery({
                list,
                levelid,
                page: 1,
                per_page: 50,
                submitter_filter: '',
            }),
        );
        queryClient.prefetchQuery(levelHistoryQuery({ list, levelid }));
        if (hasScope(auth, 'level_notes_modify')) {
            queryClient.prefetchQuery(levelNotesQuery({ list, levelid }));
        }
        queryClient.prefetchQuery(levelUpdatesQuery({ list, levelid }));

        return level;
    },
    head: ({ loaderData }) => {
        const position = loaderData?.position
            ? `#${loaderData.position} - `
            : '';
        const name = loaderData?.name
            ? `${position}${loaderData?.name}`
            : 'Unknown Level';
        return {
            meta: seo({
                title: name,
                description:
                    trimStringToNull(loaderData?.description) ??
                    'No description was provided for this level yet.',
                image: loaderData?.level_id
                    ? getFullLevelThumbnail(loaderData?.level_id)
                    : undefined,
                imageAlt: `Thumbnail for ${name}`,
                video:
                    (loaderData?.verifications?.length ?? 0) > 0
                        ? loaderData?.verifications[0].video_url
                        : undefined,
            }),
        };
    },
    component: Level,
    notFoundComponent: () =>
        DefaultNotFound({ customNotFound: 'Level not found' }),
});

function Level() {
    const { resetBackground, setBackground, setOpacity } = useBackground();

    const level = Route.useLoaderData();

    useEffect(() => {
        setBackground(getFullLevelThumbnail(level.level_id));
        setOpacity('0.05');
        return () => {
            resetBackground();
        };
    }, [level, setBackground, setOpacity, resetBackground]);

    return [
        <div
            key="level-info"
            className="flex shrink-0 snap-start w-screen lg:shrink lg:basis-2/4 lg:min-w-0 lg:w-max lg:snap-none lg:flex-col lg:flex-2"
        >
            <Suspense fallback={<Load />}>
                <LevelInfo />
            </Suspense>
        </div>,
        <div
            key="level-records"
            className="shrink-0 snap-center w-screen lg:shrink lg:basis-1/4 lg:min-w-0 lg:w-max lg:snap-none lg:flex-col lg:flex-1"
        >
            <Suspense fallback={<Load />}>
                <RecordsInfo />
            </Suspense>
        </div>,
    ];
}
